import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Switch,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  Dialog,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Dashboard as DashboardIcon,
  FamilyRestroom as FamilyIcon,
  Assessment as AssessmentIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Link as LinkIcon,
  Badge as BadgeIcon,
  Timeline as TimelineIcon,
  AccountTree as AccountTreeIcon,
  GroupAdd as GroupAddIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  LocationOn as LocationOnIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import API_BASE_URL from '../config';
import axios from 'axios';
import InvoiceModal from '../components/InvoiceModal';

// Styled Components
// const DashboardContainer = styled(Container)(({ theme }) => ({
//   paddingTop: theme.spacing(4),
//   paddingBottom: theme.spacing(4),
// }));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  height: 160,
  justifyContent: 'space-between',
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.secondary.dark, 0.1)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 30px 0 ${alpha(theme.palette.common.black, 0.1)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
  },
}));

const PREFIX_OPTIONS = ['Mr.', 'Mrs.', 'Miss', 'Dr.', 'Others'];

const normalizeStatus = (rawStatus) => {
  // UI-only normalization (no schema changes): supports legacy numeric values.
  if (rawStatus === 'active' || rawStatus === 'inactive') return rawStatus;
  if (rawStatus === '0' || rawStatus === 0 || rawStatus === null || rawStatus === undefined) return 'active';
  if (rawStatus === '1' || rawStatus === 1) return 'inactive';
  return String(rawStatus);
};

const StatusToggleCell = ({ uid, status, isUpdating, onToggle }) => {
  const isActive = status === 'active';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        label={isActive ? 'Active' : 'Inactive'}
        size="small"
        sx={{
          fontWeight: 700,
          backgroundColor: isActive ? 'rgba(46,125,50,0.15)' : 'rgba(158,158,158,0.18)',
          color: isActive ? '#2e7d32' : '#616161',
          border: `1px solid ${isActive ? '#2e7d32' : '#9e9e9e'}`,
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Switch
          checked={isActive}
          onChange={() => onToggle(uid, status)}
          disabled={isUpdating}
          inputProps={{ 'aria-label': `Set user ${uid} ${isActive ? 'inactive' : 'active'}` }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2e7d32' },
            '& .MuiSwitch-track': { backgroundColor: isActive ? '#2e7d32' : '#9e9e9e' },
          }}
        />
        {isUpdating && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Box>
    </Box>
  );
};

const AdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  // Per-row in-flight lock to prevent duplicate API calls on rapid clicks.
  const [statusUpdatingByUid, setStatusUpdatingByUid] = useState({});
  const statusUpdatingRef = useRef(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    familyTrees: 0,
    totalConnections: 0
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    display_name: '',
    email_id: '',
    phone_number: '',
    uid: '',
    gender: '',
    role: '0',
    password: ''
  });

  // Add these new state variables at the top of the AdminDashboard component
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmColor: 'error' });

  // Fetch users and stats
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/user/id`, {
        withCredentials: true
      });

      if (!response.data || !response.data.userId) {
        throw new Error('User not authenticated');
      }

      // Fetch users via admin endpoint (includes prefix, status, all fields)
      const usersResponse = await axios.get(`${API_BASE_URL}/admin/users`, {
        withCredentials: true
      });
      console.log('usersResponse', usersResponse.data);

      if (!usersResponse.data) {
        throw new Error('Failed to fetch users');
      }

      const usersWithStatus = usersResponse.data.map(user => {
        // Normalize legacy numeric status values to string
        return { ...user, status: normalizeStatus(user.status) };
      });

      setUsers(usersWithStatus);

      // Fetch statistics
      const statsResponse = await axios.get(`${API_BASE_URL}/stats`, {
        withCredentials: true
      });

      if (!statsResponse.data) {
        throw new Error('Failed to fetch statistics');
      }
      setStats(statsResponse.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message);
      if (error.response?.status === 401) {
        setSnackbar({ open: true, message: 'Session expired. Please login again.', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
      }
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Table columns configuration
  const columns = [
    { 
      field: 'uid', 
      headerName: 'UID', 
      width: 240,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
          }
        }}>
          <BadgeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'prefix',
      headerName: 'Prefix',
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{params.value || '—'}</Typography>
      ),
    },
    { 
      field: 'first_name', 
      headerName: 'First Name', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ 
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.grey[200], 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[300], 0.5),
          }
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'last_name', 
      headerName: 'Last Name', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ 
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.grey[200], 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[300], 0.5),
          }
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'email_id', 
      headerName: 'Email', 
      width: 220,
      renderCell: (params) => (
        <Box sx={{ 
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.grey[200], 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[300], 0.5),
          }
        }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'phone_number', 
      headerName: 'Phone', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ 
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.grey[200], 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[300], 0.5),
          }
        }}>
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'gender', 
      headerName: 'Gender', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small"
          sx={{ 
            backgroundColor: params.value === 'Male' ? 'primary.main' : 'secondary.main',
            color: 'white',
            fontWeight: 600,
          }}
        />
      ),
    },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 100,
      renderCell: (params) => {
        // Conditional rendering based on role
        switch(params.value) {
          case '0':
            return <Chip label="User" color="default" size="small" />;
          case '1':
            return <Chip label="Admin" color="primary" size="small" />;
          case '2':
            return <Chip label="Manager" color="secondary" size="small" />;
          default:
            return <Chip label={params.value} variant="outlined" size="small" />;
        }
      },
    },
    { 
      field: 'subscription', 
      headerName: 'Subscription', 
      width: 180,
      renderCell: (params) => {
        const getSubscriptionConfig = (status) => {
          switch(status) {
            case '1':
              return {
                label: 'Active',
                color: theme.palette.success.main,
                icon: <CheckCircleIcon sx={{ fontSize: 18 }} />
              };
            case '2':
              return {
                label: 'Expired',
                color: theme.palette.error.main,
                icon: <CancelIcon sx={{ fontSize: 18 }} />
              };
            case '3':
              return {
                label: 'Pending',
                color: theme.palette.warning.main,
                icon: <PendingIcon sx={{ fontSize: 18 }} />
              };
            default:
              return {
                label: 'Free',
                color: theme.palette.grey[500],
                icon: <PersonIcon sx={{ fontSize: 18 }} />
              };
          }
        };

        const config = getSubscriptionConfig(params.value);
        
        return (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '15px 30px',
            borderRadius: '6px',
            backgroundColor: alpha(config.color, 0.1),
            '&:hover': {
              backgroundColor: alpha(config.color, 0.2),
            }
          }}>
            {config.icon}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: config.color
              }}
            >
              {config.label}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      renderCell: (params) => (
        <StatusToggleCell
          uid={params.row.uid || params.row.id}
          status={normalizeStatus(params.row.status)}
          isUpdating={!!statusUpdatingByUid[(params.row.uid || params.row.id)]}
          onToggle={toggleUserStatus}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          padding: '15px 30px',
          borderRadius: '6px',
          backgroundColor: alpha(theme.palette.grey[200], 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[300], 0.5),
          }
        }}>
          <ActionButton 
            onClick={() => handleView(params.row.uid)}
            size="small"
            color="info"
            title="View Profile"
          >
            <VisibilityIcon />
          </ActionButton>
          <ActionButton 
            onClick={() => handleOpenInvoice(params.row)}
            size="small"
            color="success"
            title="View Invoice"
            sx={{ color: theme.palette.success.main }}
          >
            <ReceiptIcon />
          </ActionButton>
          <ActionButton 
            onClick={() => handleEdit(params.row.uid)}
            size="small"
            color="primary"
            title="Edit User"
          >
            <EditIcon />
          </ActionButton>
          <ActionButton 
            onClick={() => handleDelete(params.row.uid)}
            size="small"
            color="error"
            title="Delete User"
          >
            <DeleteIcon />
          </ActionButton>
        </Box>
      ),
    },
  ];

  // Handler functions
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && paymentHistory.length === 0) {
      fetchPaymentHistory();
    }
  };

  const fetchPaymentHistory = async () => {
    setPaymentLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/payment-history`, { withCredentials: true });
      setPaymentHistory(res.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleOpenInvoice = async (row) => {
    setInvoiceModalOpen(true);
    setInvoiceLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/invoices`, { withCredentials: true });
      const invoices = res.data || [];
      // Match logic: Priority 1 (Specific Payment/Order ID), Priority 2 (Latest UID match for User Management list)
      const match = invoices.find(inv =>
        (row.payment_id && inv.payment_id === row.payment_id) || 
        (row.order_id && inv.order_id === row.order_id)
      ) || invoices.find(inv => 
        (row.uid && inv.uid === row.uid)
      );
      setSelectedInvoice(match || null);
    } catch (err) {
      console.error("Fetch invoices error:", err);
      showSnackbar("Failed to load invoice. Please try again.", "error");
      setSelectedInvoice(null);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleEdit = async (uid) => {
    try {
      // Use the admin endpoint to fetch specific user's data
      const response = await axios.get(`${API_BASE_URL}/admin/users/${uid}`, {
        withCredentials: true
      });
      
      const userData = response.data;
      console.log('userData to edit:', userData);
      
      // Update form data with the fetched user data
      setFormData({
        prefix: userData.prefix || '',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        display_name: userData.display_name || '',
        email_id: userData.email_id || '',
        phone_number: userData.phone_number || '',
        uid: userData.uid || '',
        gender: userData.gender || '',
        role: userData.role || '0',
        status: userData.status || 'active',
        subscription: userData.subscription || '0'
      });
      
      setSelectedUser(userData);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error.response?.status === 401) {
        showSnackbar('Authentication failed. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        showSnackbar('Error fetching user data', 'error');
      }
    }
  };

  const handleDelete = async (uid) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message: 'Are you sure you want to permanently delete this user? This action cannot be undone.',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/admin/users/${uid}`, { withCredentials: true });
          showSnackbar('User deleted successfully');
          fetchDashboardData();
        } catch (error) {
          showSnackbar('Error deleting user', 'error');
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.uid}`, formData, { withCredentials: true });
        showSnackbar('User updated successfully');
      } else {
        const response = await axios.post(`${API_BASE_URL}/admin/users`, formData, { withCredentials: true });
        const msg = response.data.generatedPassword
          ? `User created! Default password: ${response.data.generatedPassword}`
          : 'User created successfully';
        showSnackbar(msg);
      }
      setOpenDialog(false);
      fetchDashboardData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving user', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleUserStatus = async (uid, currentStatusRaw) => {
    const currentStatus = normalizeStatus(currentStatusRaw);
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';

    if (!uid) {
      showSnackbar('Invalid user ID', 'error');
      return;
    }

    // Avoid duplicate API calls for the same user if clicked rapidly.
    // Use a ref so this is synchronous (doesn't depend on React state timing).
    if (statusUpdatingRef.current.has(uid)) return;
    statusUpdatingRef.current.add(uid);

    // Optimistic UI: apply immediately, revert if the backend update fails.
    setStatusUpdatingByUid(prev => ({ ...prev, [uid]: true }));
    setUsers(prev => prev.map(u => (u.uid === uid ? { ...u, status: nextStatus } : u)));

    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/users/update-status/${uid}`,
        { status: nextStatus },
        { withCredentials: true }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to update status');
      }

      showSnackbar('Status updated successfully', 'success');
    } catch (error) {
      setUsers(prev => prev.map(u => (u.uid === uid ? { ...u, status: currentStatus } : u)));
      showSnackbar(error.response?.data?.message || error.message || 'Error updating status', 'error');
    } finally {
      statusUpdatingRef.current.delete(uid);
      setStatusUpdatingByUid(prev => {
        const copy = { ...prev };
        delete copy[uid];
        return copy;
      });
    }
  };

  // Add this new function to fetch user details
  const handleView = async (uid) => {
    try {
      setLoadingUserDetails(true);
      setViewDialogOpen(true);
      
      // Fetch user basic details
      const userResponse = await axios.get(`${API_BASE_URL}/admin/users/${uid}`, {
        withCredentials: true
      });
      
      // Add withCredentials to all requests
      const [educationResponse, jobsResponse, addressesResponse, customFieldsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/education/${userResponse.data.id}`, {
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/jobs/${userResponse.data.id}`, {
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/addresses/${userResponse.data.id}`, {
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/custom_fields/${userResponse.data.id}`, {
          withCredentials: true
        })
      ]);
      
      setSelectedUserDetails({
        ...userResponse.data,
        education: educationResponse.data.educationDetails || [],
        jobs: jobsResponse.data || [],
        // FIXED: backend returns { addresses: [...] } not a plain array
        addresses: addressesResponse.data.addresses || [],
        customFields: customFieldsResponse.data || []
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Add more detailed error handling
      if (error.response?.status === 401) {
        showSnackbar('Authentication failed. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        showSnackbar(`Error fetching user details: ${error.message}`, 'error');
      }
    } finally {
      setLoadingUserDetails(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Admin Dashboard
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={500}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon sx={{ fontSize: 32, color: 'white' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {stats.totalUsers}
              </Typography>
            </StatsCard>
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={700}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Active Users
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {stats.activeUsers}
              </Typography>
            </StatsCard>
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={900}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FamilyIcon sx={{ fontSize: 32, color: 'white' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Family Trees
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {stats.familyTrees}
              </Typography>
            </StatsCard>
          </Fade>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1100}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinkIcon sx={{ fontSize: 32, color: 'white' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Total Relationships
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {stats.totalConnections}
              </Typography>
            </StatsCard>
          </Fade>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper 
        sx={{ 
          width: '100%', 
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTabs-indicator': {
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                height: 3,
              },
            }}
          >
            <StyledTab icon={<PeopleIcon />} label="Users Management" />
            <StyledTab icon={<AssessmentIcon />} label="Payment History" />
            {/* <StyledTab icon={<FamilyIcon />} label="Family Trees" />
            <StyledTab icon={<AssessmentIcon />} label="Reports" /> */}
          </Tabs>
        </Box>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setSelectedUser(null);
                setFormData({
                  prefix: '',
                  first_name: '',
                  last_name: '',
                  display_name: '',
                  email_id: '',
                  phone_number: '',
                  uid: '',
                  gender: '',
                  role: '0',
                  password: ''
                });
                setOpenDialog(true);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                }
              }}
            >
              Add User
            </Button>
            <TextField
              size="small"
              placeholder="Search users..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ 
                width: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Box>

<Box sx={{ height: 600, width: '100%', overflowX: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={users.filter(user => {
                  if (!searchQuery) return true;
                  const searchLower = searchQuery.toLowerCase();
                  return (
                    (user.uid?.toString() || '').toLowerCase().includes(searchLower) ||
                    (user.first_name || '').toLowerCase().includes(searchLower) ||
                    (user.last_name || '').toLowerCase().includes(searchLower) ||
                    (user.email_id || '').toLowerCase().includes(searchLower) ||
                    (user.phone_number || '').toLowerCase().includes(searchLower) ||
                    (user.gender || '').toLowerCase().includes(searchLower) ||
                    (user.role || '').toLowerCase().includes(searchLower)
                  );
                })}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection
                disableSelectionOnClick
                components={{
                  Toolbar: GridToolbar,
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              />
            )}
          </Box>
        </TabPanel>

        {/* Payment History Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Payment History</Typography>
            {paymentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : paymentHistory.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                No payment records found.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: alpha(theme.palette.primary.main, 0.08) }}>
                      {['User Name', 'UID', 'Email', 'Plan', 'Amount (₹)', 'Status', 'Date', 'Invoice'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, borderBottom: `2px solid ${alpha(theme.palette.divider, 0.2)}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((row, i) => (
                      <tr key={row.id} style={{ background: i % 2 === 0 ? 'transparent' : alpha(theme.palette.primary.main, 0.03) }}>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>{row.user_name}</td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, fontFamily: 'monospace', fontSize: 12 }}>{row.uid}</td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>{row.email}</td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>{row.plan}</td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, fontWeight: 700 }}>₹{row.amount}</td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                          <Chip
                            label={row.status}
                            size="small"
                            color={row.status === 'success' ? 'success' : row.status === 'failed' ? 'error' : 'default'}
                            sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                          />
                        </td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                          {new Date(row.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 16px', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleOpenInvoice(row)}
                            sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: 12, fontWeight: 'bold' }}
                          >
                            Invoice
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Family Trees Tab */}
        {/* <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <FamilyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 2,
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Family Trees Management
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                A comprehensive system for managing and visualizing family relationships, genealogy tracking, and family tree creation.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Fade in timeout={500}>
                  <FeatureCard>
                    <AccountTreeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Tree Visualization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Interactive family tree visualization with drag-and-drop functionality, zoom controls, and relationship mapping.
                    </Typography>
                  </FeatureCard>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={700}>
                  <FeatureCard>
                    <TimelineIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Genealogy Tracking
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Track family history, important dates, and events across generations with detailed timeline views.
                    </Typography>
                  </FeatureCard>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={900}>
                  <FeatureCard>
                    <GroupAddIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Member Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add, edit, and manage family members with detailed profiles, relationships, and permissions.
                    </Typography>
                  </FeatureCard>
                </Fade>
              </Grid>
            </Grid>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                Coming Soon
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
                We're working hard to bring you these exciting features. Stay tuned for updates!
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                Get Notified
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* Reports Tab */}
        {/* <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AssessmentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Reports and Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This feature is coming soon. Stay tuned for updates!
            </Typography>
          </Box>
        </TabPanel> */}
      </Paper>

      {/* Add/Edit User Dialog */}
      <StyledDialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {selectedUser ? 'Edit User' : 'Add New User'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Prefix"
                  value={formData.prefix}
                  onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {PREFIX_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email_id}
                  onChange={(e) => setFormData({ ...formData, email_id: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </TextField>
              </Grid>
              {/* <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="UID"
                  value={formData.uid}
                  onChange={(e) => setFormData({ ...formData, uid: e.target.value })}
                  required={!selectedUser}
                  disabled={!!selectedUser}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid> */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="1">Admin</MenuItem>
                  <MenuItem value="0">User</MenuItem>
                </TextField>
              </Grid>
              {!selectedUser && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              )}
              {selectedUser && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                onClick={() => setOpenDialog(false)}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  }
                }}
              >
                {selectedUser ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        </form>
      </StyledDialog>

      {/* User Details Dialog */}
      <StyledDialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            User Details
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          {loadingUserDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedUserDetails ? (
            <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Basic Information</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">UID</Typography>
                        <Typography>{selectedUserDetails.uid}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                        <Typography>
                          {`${selectedUserDetails.prefix || ''} ${selectedUserDetails.first_name} ${selectedUserDetails.middle_name || ''} ${selectedUserDetails.last_name}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                        <Typography>{selectedUserDetails.email_id}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                        <Typography>{selectedUserDetails.phone_number}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                        <Typography>{selectedUserDetails.gender}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                        <Typography>{selectedUserDetails.age}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Address Information */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Address</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Current Address</Typography>
                        <Typography>
                          {`${selectedUserDetails.address}, ${selectedUserDetails.area}, ${selectedUserDetails.district}, ${selectedUserDetails.state} - ${selectedUserDetails.pincode}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">Village</Typography>
                        <Typography>{selectedUserDetails.village_name}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                {selectedUserDetails.addresses?.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>More Address</Typography>
                      {selectedUserDetails.addresses.map((add, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">{add.type}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`${add.address} (${add.district} -${add.state} -${add.pincode})`}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
                {/* Education Details */}
                {selectedUserDetails.education?.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Education</Typography>
                      {selectedUserDetails.education.map((edu, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">{edu.level}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`${edu.institute} (${edu.start_year} -${edu.end_year})`}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}

                {/* Job Details */}
                {selectedUserDetails.jobs?.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Employment History</Typography>
                      {selectedUserDetails.jobs.map((job, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1">{job.type}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`${job.company_name} (${job.start_year} - ${job.end_year || 'Present'})`}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}

                {/* Custom Fields */}
                {selectedUserDetails.customFields?.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>Additional Information</Typography>
                      <Grid container spacing={2}>
                        {selectedUserDetails.customFields.map((field, index) => (
                          <Grid item xs={6} key={index}>
                            <Typography variant="subtitle2" color="text.secondary">
                             {` ${field.title}  ${field.field_name}`}
                            </Typography>
                            <Typography>{field.field_value}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography color="text.secondary">No user details available</Typography>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => setViewDialogOpen(false)}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </StyledDialog>

      {/* Custom Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.2)}`,
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
          p: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent'
            }}>
              {confirmDialog.title}
            </Typography>
            <IconButton size="small" onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            {confirmDialog.message}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={confirmDialog.confirmColor || 'error'}
              onClick={confirmDialog.onConfirm}
              sx={{
                borderRadius: 2, textTransform: 'none', px: 3,
                boxShadow: `0 4px 12px ${alpha(theme.palette[confirmDialog.confirmColor || 'error'].main, 0.4)}`,
                '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 6px 16px ${alpha(theme.palette[confirmDialog.confirmColor || 'error'].main, 0.5)}` }
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog>

      <InvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        invoiceData={selectedInvoice}
        loading={invoiceLoading}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ 
            borderRadius: 2,
            boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
   </Box>
  );
};

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

export default AdminDashboard; 
